// Converts a PDF File to a combined JPEG base64 image (all pages stacked vertically).
// Requires pdfjs-dist. Worker is loaded from CDN to avoid bundler issues with CRA.

export async function pdfToImage(file, scale = 2) {
  const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.js');

  // Use CDN worker matching installed v3 — avoids webpack bundling issues in CRA
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js`;
  }

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const canvases = [];
  for (let i = 1; i <= Math.min(pdf.numPages, 6); i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    canvases.push(canvas);
  }

  if (canvases.length === 0) throw new Error('PDF vide');

  // Stack all pages vertically with a small gap
  const GAP = 8;
  const totalWidth = Math.max(...canvases.map(c => c.width));
  const totalHeight = canvases.reduce((h, c) => h + c.height, 0) + GAP * (canvases.length - 1);

  const combined = document.createElement('canvas');
  combined.width = totalWidth;
  combined.height = totalHeight;
  const ctx = combined.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, totalWidth, totalHeight);

  let y = 0;
  for (const canvas of canvases) {
    ctx.drawImage(canvas, 0, y);
    y += canvas.height + GAP;
  }

  return {
    data: combined.toDataURL('image/jpeg', 0.88),
    mime: 'image/jpeg',
    nom: file.name.replace(/\.pdf$/i, '.jpg'),
  };
}

export function isPdf(file) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

// Groq base64 limit = 4MB → keep image binary under 2.8MB (base64 ≈ +33%)
const GROQ_MAX_BYTES = 2.8 * 1024 * 1024;

export async function compressForGroq(dataUrl) {
  const base64 = dataUrl.split(',')[1] || '';
  const bytes = Math.round(base64.length * 0.75);
  if (bytes <= GROQ_MAX_BYTES) return dataUrl;

  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const ratio = Math.sqrt(GROQ_MAX_BYTES / bytes);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.src = dataUrl;
  });
}
